import type React from "react";
import { ReactionPopup } from "@/components/common/ReactionPopup";
import { Button } from "@/components/ui/button";
import type { ReactionType } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface ReactionButtonProps {
  localReaction: ReactionType | null;
  reactionText: string;
  showReactionLabel: boolean;
  inactiveReactionIconOpacityClass: string;
  size?: "md" | "sm";
  isPending: boolean;
  isActionDisabled: boolean;
  showReactions: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onReactionSelect: (selectedReaction: ReactionType) => Promise<void>;
  onReactionClear: () => Promise<void>;
}

export const ReactionButton: React.FC<ReactionButtonProps> = ({
  localReaction,
  reactionText,
  showReactionLabel,
  inactiveReactionIconOpacityClass,
  size = "md",
  isPending,
  isActionDisabled,
  showReactions,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onReactionSelect,
  onReactionClear,
}) => {
  const buttonSizeClass = size === "sm" ? "h-6 px-1 text-xs" : "";
  const reactionIconSizeClass = size === "sm" ? "h-5 w-5" : "size-5";

  return (
    <div
      className="relative"
      onMouseEnter={!isPending && !isActionDisabled ? onMouseEnter : undefined}
      onMouseLeave={!isPending && !isActionDisabled ? onMouseLeave : undefined}
    >
      <Button
        variant="ghost"
        disabled={isActionDisabled}
        onClick={onClick}
        className={buttonSizeClass}
        size={size === "sm" ? "sm" : "default"}
      >
        <img
          src="/reaction.svg"
          alt="reaction"
          className={cn(
            "block shrink-0 translate-y-0.5",
            reactionIconSizeClass,
            inactiveReactionIconOpacityClass,
          )}
        />
        {showReactionLabel && (
          <span
            className={cn(
              "ml-2",
              size === "sm" ? "font-sub text-xs" : undefined,
              localReaction ? "text-blue-400" : undefined,
              !localReaction ? "text-rw-dark-gray" : undefined,
            )}
          >
            {reactionText}
          </span>
        )}
      </Button>

      <ReactionPopup
        onReactionSelect={(selectedReaction) => {
          void onReactionSelect(selectedReaction);
        }}
        onReactionClear={() => {
          void onReactionClear();
        }}
        currentReaction={localReaction}
        isVisible={showReactions && !isPending && !isActionDisabled}
      />
    </div>
  );
};
